import { ArrowRight, ChevronDown, MapPin, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { EventInstanceRecord } from '../api/types';
import { pickLocalized } from './localized-text';
import { eventDateParts, eventTime } from './branding-event-date';
import { BrandingWordmark, studioPaletteStyle, type StudioEventsViewProps } from './organization-branding-view';
import heroArtwork from '../../assets/branding/editorial-hero-v1.webp';
import { BrandingHeroImage } from './branding-hero-image';
import './editorial-events.css';

const COPY = {
  cs: { events: 'Akce', enrollments: 'Moje účast', tasks: 'Úkoly', main: 'Hlavní událost', next: 'Další události',
    program: 'Zobrazit program', empty: 'Nová setkání se připravují', emptyBody: 'Jakmile organizace zveřejní akci, najdete ji zde.',
    dateMissing: 'Termín připravujeme', locationMissing: 'Místo bude upřesněno', contact: 'Kontakt', website: 'Web organizace', profile: 'Uživatelský účet', language: 'Čeština' },
  en: { events: 'Events', enrollments: 'My attendance', tasks: 'Tasks', main: 'Featured event', next: 'More events',
    program: 'View programme', empty: 'New gatherings are coming', emptyBody: 'Events will appear here when your organization publishes them.',
    dateMissing: 'Date to be confirmed', locationMissing: 'Venue to be confirmed', contact: 'Contact', website: 'Organization website', profile: 'User account', language: 'English' },
} as const;

function EditorialDate({ event, locale }: { event: EventInstanceRecord; locale: 'cs' | 'en' }) {
  const date = eventDateParts(event, locale);
  return <div className="editorial-date">{date ? <>
    <strong>{date.day}{locale === 'cs' ? '.' : ''}</strong><span>{date.month}</span><span>{date.year}{date.utcFallback && <small> UTC</small>}</span>
  </> : <span className="editorial-date--missing">{COPY[locale].dateMissing}</span>}</div>;
}

export function EditorialEventsView({ branding, events, locale, preview = false, logoUrl, heroUrl, includeHeader = preview }: StudioEventsViewProps) {
  const copy = COPY[locale];
  const ordered = [...events].sort((a, b) => eventTime(a) - eventTime(b) || a.code.localeCompare(b.code));
  const first = ordered[0];
  const intro = pickLocalized(branding.intro, locale);
  const tagline = pickLocalized(branding.tagline, locale);
  return <div className="editorial-view" style={studioPaletteStyle(branding.paletteId)}>
    {includeHeader && <header className="studio-header studio-header--preview editorial-header">
      <div className="studio-wordmark"><BrandingWordmark branding={branding} logoUrl={logoUrl} /></div>
      <nav aria-label={copy.events} className="studio-links"><button disabled type="button">{copy.events}</button><button disabled type="button">{copy.enrollments}</button><button disabled type="button">{copy.tasks}</button></nav>
      <span className="studio-preview-locale">{copy.language}<ChevronDown size={14} aria-hidden="true" /></span>
      <span className="studio-avatar" aria-label={copy.profile}><UserRound size={18} aria-hidden="true" /></span>
    </header>}
    <section className={`editorial-hero${first ? '' : ' editorial-hero--empty'}`}>
      <div className="editorial-hero__copy">
        <p className="editorial-eyebrow">{first ? copy.main : tagline || copy.events}</p>
        <div className="editorial-hero__event">
          {first && <div className="editorial-hero__date"><EditorialDate event={first} locale={locale} /><p><MapPin size={19} aria-hidden="true" />{first.locationName || copy.locationMissing}</p></div>}
          <div className="editorial-hero__detail">
            <h1>{first ? pickLocalized(first.titles, locale) : copy.empty}</h1>
            {intro && <p className="editorial-intro">{intro}</p>}
            {first ? (preview ? <button className="editorial-cta" disabled type="button">{copy.program}<ArrowRight size={24} aria-hidden="true" /></button> :
              <Link className="editorial-cta" to={`/attendee/events/${first.id}`}>{copy.program}<ArrowRight size={24} aria-hidden="true" /></Link>) : <p className="editorial-empty">{copy.emptyBody}</p>}
          </div>
        </div>
      </div>
      <div className="editorial-hero__art"><BrandingHeroImage overrideUrl={heroUrl} defaultUrl={heroArtwork} alt={branding.heroAlt} /></div>
    </section>
    {ordered.length > 1 && <section className="editorial-more" aria-label={copy.next}>
      <div className="editorial-section-heading"><h2>{copy.next}</h2>{tagline && <p>{tagline}</p>}</div>
      <ul className="editorial-event-list">
        {ordered.slice(1).map((event) => {
          const description = pickLocalized(event.summaries, locale) || pickLocalized(event.descriptions, locale);
          const title = pickLocalized(event.titles, locale);
          return <li key={event.id}>
            <EditorialDate event={event} locale={locale} />
            <div className="editorial-event-detail"><h3>{preview ? title : <Link to={`/attendee/events/${event.id}`}>{title}</Link>}</h3>{description && <p>{description}</p>}</div>
            <p className="editorial-event-location"><MapPin size={19} aria-hidden="true" />{event.locationName || copy.locationMissing}</p>
            {preview ? <span className="editorial-event-arrow" aria-hidden="true"><ArrowRight size={24} /></span> : <Link className="editorial-event-arrow" to={`/attendee/events/${event.id}`} aria-label={`${copy.program}: ${title}`}><ArrowRight size={24} aria-hidden="true" /></Link>}
          </li>;
        })}
      </ul>
    </section>}
    {(branding.website || branding.contactEmail) && <footer className="editorial-contact">
      {branding.website && (preview ? <span>{copy.website}: {branding.website}</span> : <a href={branding.website} target="_blank" rel="noopener noreferrer">{copy.website}<ArrowRight size={16} aria-hidden="true" /></a>)}
      {branding.contactEmail && (preview ? <span>{copy.contact}: {branding.contactEmail}</span> : <a href={`mailto:${encodeURIComponent(branding.contactEmail)}`}>{copy.contact}: {branding.contactEmail}</a>)}
    </footer>}
  </div>;
}
